import React from "react";
import { AppContext } from "../store/genesys.context";
import { useTeaserRevealed } from "../store/teaserReveal.context";
import debugLog from "../utils/debugLog";
import {
  HOST_MESSAGES,
  requestWindowSize,
  sendIframeStyles,
  type ParentViewport,
} from "./hostProtocol";
import {
  computeIframeSize,
  WIDGET_GEOMETRY,
  type WidgetShape,
} from "./widgetGeometry";

/** Marks the teaser popup so the bridge can measure its rendered height. */
export const POPUP_MEASURE_ATTRIBUTE = "data-widget-popup";

/**
 * Keeps the iframe on the host page the size of the widget inside it.
 *
 * Renders nothing and only exists in the standalone (iframe) deployment — the
 * npm package mounts the widget into the host DOM, where it sizes itself.
 * Everything is derived from the widget state in one place: the scattered
 * imperative `sendIframeSize(...)` calls of the pre-package version are exactly
 * what rotted away, and a state-driven effect cannot fall out of sync.
 */
const HostBridge = (): null => {
  const {
    minimizeBot,
    showMinimizeBotPopup,
    hideLauncher,
  }: {
    minimizeBot: boolean;
    showMinimizeBotPopup: boolean;
    hideLauncher: boolean;
  } = React.useContext(AppContext);
  const teaserRevealed = useTeaserRevealed();

  const [parentViewport, setParentViewport] = React.useState<
    ParentViewport | undefined
  >(undefined);

  const shape: WidgetShape = !minimizeBot
    ? "window"
    : hideLauncher
      ? "hidden"
      : showMinimizeBotPopup && teaserRevealed
        ? "teaser"
        : "bubble";

  // The host answers this once and then again on every window resize, so a
  // single subscription covers both the initial size and later changes.
  React.useEffect(() => {
    const onHostMessage = (event: MessageEvent): void => {
      if (event.data?.type !== HOST_MESSAGES.responseWindowSize) return;

      const { width, height } = event.data;

      if (typeof width !== "number" || typeof height !== "number") return;

      setParentViewport((current) =>
        current?.width === width && current?.height === height
          ? current
          : { width, height },
      );
    };

    window.addEventListener("message", onHostMessage);
    requestWindowSize();

    return () => window.removeEventListener("message", onHostMessage);
  }, []);

  React.useEffect(() => {
    // The teaser's height depends on its copy, so measure it rather than
    // hardcoding a second number next to the styles. Fixed positioning means
    // the element has its full layout size even while the iframe still clips it.
    const popup = document.querySelector(`[${POPUP_MEASURE_ATTRIBUTE}]`);
    const popupHeight =
      shape === "teaser" && popup
        ? Math.ceil(popup.getBoundingClientRect().height)
        : undefined;

    const { width, height } = computeIframeSize(
      shape,
      parentViewport,
      popupHeight,
    );

    debugLog(`Sizing iframe for "${shape}": ${width}x${height}`);

    sendIframeStyles({
      width: `${width}px`,
      height: `${height}px`,
      border: "none",
      // The window rounds its own corners; the iframe must not clip them.
      borderRadius: `${WIDGET_GEOMETRY.margin}px`,
      position: "fixed",
      bottom: "0px",
      right: "0px",
      zIndex: 99999,
    });
  }, [shape, parentViewport]);

  return null;
};

export default HostBridge;
